/** Block types supported in AOP markdown code */
export enum ECodeBlockType {
  HEADING = 'heading',
  PARAGRAPH = 'paragraph',
  ORDERED_LIST = 'orderedList',
  UNORDERED_LIST = 'unorderedList',
  TABLE = 'table',
}

/** Diff status for a block comparison */
export enum EDiffStatus {
  UNCHANGED = 'unchanged',
  ADDED = 'added',
  REMOVED = 'removed',
  MODIFIED = 'modified',
}

/** Diff mark types for TipTap */
export enum EDiffMark {
  ADDED = 'diffAdded',
  DELETED = 'diffDeleted',
}

/** Represents a parsed block from AOP markdown code */
export interface ICodeBlock {
  type: ECodeBlockType;
  /** Heading level (1-6), only applicable for heading blocks */
  level?: number;
  /**
   * List nesting depth (0 = top level, 1 = first indent, …), only meaningful
   * for ORDERED_LIST / UNORDERED_LIST blocks. Derived from the item's leading
   * indentation so nested sub-steps aren't flattened to top level.
   */
  depth?: number;
  content: string;
  /**
   * Parsed rows for TABLE blocks — each row is an array of cell text, header
   * row first, alignment/separator row excluded. `content` still holds the
   * raw markdown for equality checks and non-table rendering fallbacks.
   */
  rows?: string[][];
}

/** Represents a diff result for a single block */
export interface IBlockDiff {
  status: EDiffStatus;
  blockType: ECodeBlockType;
  level?: number;
  /** List nesting depth (0-based); carried through from ICodeBlock.depth. */
  depth?: number;
  oldContent?: string;
  newContent?: string;
  /** Row-level diff, only populated for TABLE blocks. */
  rowDiffs?: IRowDiff[];
}

/** Diff result for a single table row, cell-by-cell. */
export interface IRowDiff {
  status: EDiffStatus;
  /** True for the header row. */
  isHeader: boolean;
  oldCells?: string[];
  newCells?: string[];
}

/** A section of the document starting with a header */
export interface ISection {
  header: ICodeBlock | null;
  content: ICodeBlock[];
}

/**
 * One navigable change in a diff — a single block that was added, removed, or
 * modified. `index` is a sequential id (0-based, in document order over
 * changed blocks only) that matches the `data-diff-anchor` attribute stamped
 * on the corresponding rendered node, so a sidebar list item can scroll the
 * diff document to its block. UNCHANGED blocks never produce a summary.
 */
export interface IDiffChangeSummary {
  index: number;
  /**
   * Last anchor index this entry covers, inclusive. For a standalone change
   * `endIndex === index`; for a collapsed section it spans the heading plus all
   * absorbed child blocks, so hovering the entry can highlight the whole
   * section (indices `index..endIndex`) rather than just the heading.
   */
  endIndex: number;
  status: EDiffStatus.ADDED | EDiffStatus.REMOVED | EDiffStatus.MODIFIED;
  /** Short, markdown-stripped snippet of the changed block for the list row. */
  label: string;
}
